<template>
    <TextboxField
        v-if="isShortTextField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            placeholder: field.Placeholder,
        }" />
    <TextboxField
        v-if="isLongTextField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            placeholder: field.Placeholder,
        }" />
    <TextboxField
        v-if="isIntegerField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            placeholder: field.Placeholder,
            hint: t({
                key: 'checkout.number_field_hint',
                values: ['54'],
            }),
        }" />
    <TextboxField
        v-if="isDecimalField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            placeholder: field.Placeholder,
            hint: t({
                key: 'checkout.number_field_hint',
                values: ['35.4'],
            }),
        }" />
    <DateInputField
        v-if="isDateField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: getDateHint(),
        }" />
    <DatetimeInputField
        v-if="isDateAndTimeField(field)"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: getDatetimeHint(),
        }" />
    <SelectField
        v-if="isBooleanField(field)"
        v-model="boolValue"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: field.Placeholder,
            items: boolItems,
        }" />
    <SelectField
        v-if="isListOfValuesField(field) && !isMultiple"
        v-model="field.Answer[0]"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: field.Placeholder,
            items: selectSimpleItems,
        }" />
    <SelectMultipleField
        v-if="isListOfValuesField(field) && isMultiple"
        v-model="field.Answer"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: field.Placeholder,
            items: selectMultipleItems,
        }" />

    <SelectField
        v-if="isListOfValuesOrTextField(field)"
        v-model="customSelectionValue"
        :name="fieldName"
        :input="{
            label: field.Question,
            hint: field.Placeholder,
            items: selectOrTextItems,
        }" />
    <TextboxField
        v-if="isListOfValuesOrTextField(field) && customSelectionTextVisible"
        v-model="customSelectionText"
        :name="customFieldName"
        :input="{
            label: field.Placeholder,
        }" />
</template>

<script lang="ts">
    import { computed, defineComponent, PropType, ref, watch } from 'vue';
    import { ActivityTicketSpecificField } from '@/api/Cart/cart.interfaces';
    import {
        formatTicketFieldName,
        getCustomAnswerField,
        getDateHint,
        getDatetimeHint,
        isBooleanField,
        isDateAndTimeField,
        isDateField,
        isDecimalField,
        isIntegerField,
        isListOfValuesField,
        isListOfValuesOrTextField,
        isLongTextField,
        isShortTextField,
        parseBoolean,
    } from '@/components/checkoutPageV3/Sections/Products/Activity/Activity.helpers';
    import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
    import { isNil } from 'lodash';
    import { isRequired } from '@ui/components/FormV2/Validations/functions';
    import { t } from '@shared/helpers/translation';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import SelectField from '@ui/components/FormV2/Fields/Select/SelectField.vue';
    import SelectMultipleField from '@ui/components/FormV2/Fields/SelectMultiple/SelectMultipleField.vue';
    import DatetimeInputField from '@ui/components/FormV2/Fields/DatetimeInput/DatetimeInputField.vue';

    const CUSTOM_PICKUP_KEY = 'CUSTOM_PICKUP';

    export default defineComponent({
        name: 'SpecificTicketField',
        components: {
            DatetimeInputField,
            SelectMultipleField,
            SelectField,
            DateInputField,
            TextboxField,
        },
        methods: {
            getDatetimeHint,
            getDateHint,
            t,
            isListOfValuesOrTextField,
            isListOfValuesField,
            isBooleanField,
            isDateAndTimeField,
            isDateField,
            isDecimalField,
            isIntegerField,
            isLongTextField,
            isShortTextField,
        },
        emits: ['addCustomField', 'removeCustomField'],
        props: {
            index: {
                type: Number,
                required: true,
            },
            field: {
                type: Object as PropType<ActivityTicketSpecificField>,
                required: true,
            },
        },
        setup(props, { emit }) {
            const fieldName = computed(() =>
                formatTicketFieldName(props.index, props.field.Id)
            );
            const customFieldName = computed(
                () => `${formatTicketFieldName(props.index, props.field.Id)}_custom`
            );
            const isMultiple = computed(() => props.field.CanSelectMultipleOptions);

            const boolValue = ref(
                isBooleanField(props.field) && parseBoolean(props.field.Answer[0])
            );

            const boolItems = [
                {
                    value: true,
                    label: t({ key: 'shared.yes' }),
                },
                {
                    value: false,
                    label: t({ key: 'shared.no' }),
                },
            ] as SelectItem[];

            const selectSimpleItems = computed<SelectItem[]>(() => {
                if (isListOfValuesField(props.field) && !isMultiple.value) {
                    return props.field.AnswerOptions.map((o) => ({
                        value: o.Id,
                        label: o.Value,
                    }));
                }
                return [];
            });

            const selectMultipleItems = computed<SelectItem[]>(() => {
                if (isListOfValuesField(props.field) && isMultiple.value) {
                    return props.field.AnswerOptions.map((o) => ({
                        value: o.Id,
                        label: o.Value,
                    }));
                }
                return [];
            });

            const selectOrTextItems = computed<SelectItem[]>(() => {
                if (isListOfValuesOrTextField(props.field)) {
                    return props.field.AnswerOptions.map((o) => ({
                        value: o.Id,
                        label: o.Value,
                    }));
                }
                return [];
            });

            const customSelectionValue = ref(getInitialCustomSelectionValue());
            const customSelectionText = ref(getInitialCustomSelectionText());
            const customSelectionTextVisible = ref(false);

            function getInitialCustomSelectionValue() {
                const answer = props.field.Answer[0];
                const options = props.field.AnswerOptions.map((o) => o.Id);

                if (isListOfValuesOrTextField(props.field)) {
                    return !isNil(answer) && !options.includes(answer)
                        ? CUSTOM_PICKUP_KEY
                        : answer;
                }
            }

            function getInitialCustomSelectionText() {
                const answer = props.field.Answer[0];

                if (isListOfValuesOrTextField(props.field)) {
                    return getInitialCustomSelectionValue() === CUSTOM_PICKUP_KEY
                        ? answer
                        : '';
                }
            }

            watch(boolValue, (newValue) => {
                if (!isNil(newValue) && isBooleanField(props.field)) {
                    props.field.Answer[0] = newValue.toString();
                }
            });

            watch(
                customSelectionValue,
                (newValue) => {
                    if (isListOfValuesOrTextField(props.field) && newValue) {
                        if (newValue !== CUSTOM_PICKUP_KEY) {
                            customSelectionTextVisible.value = false;
                            props.field.Answer[0] = newValue;

                            emit('removeCustomField', {
                                index: props.index,
                                field: getCustomAnswerField(props.field, props.index, [
                                    isRequired,
                                ]),
                            });
                        } else {
                            customSelectionTextVisible.value = true;
                            if (!isNil(customSelectionText.value)) {
                                props.field.Answer[0] = customSelectionText.value;
                            }

                            emit('addCustomField', {
                                index: props.index,
                                field: getCustomAnswerField(props.field, props.index, [
                                    isRequired,
                                ]),
                            });
                        }
                    }
                },
                { immediate: true }
            );

            watch(customSelectionText, (newValue) => {
                if (isListOfValuesOrTextField(props.field) && !isNil(newValue)) {
                    props.field.Answer[0] = newValue;
                }
            });

            return {
                fieldName,
                isMultiple,
                boolValue,
                boolItems,
                selectSimpleItems,
                selectMultipleItems,
                selectOrTextItems,
                customFieldName,
                customSelectionValue,
                customSelectionText,
                customSelectionTextVisible,
            };
        },
    });
</script>
